import React, { useState } from 'react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { FeatureGate, isLive } from '../../components/platform/FeatureStatusBadge';
import { authorEarnings, authorEntitlementSummary, authorProfile, authorTitles } from '../../data/author';
import { naira } from '../../utils/format';
import { AttentionBoard } from '../../components/operator/AttentionBoard';
import { useSectionRoute } from '../../hooks/useSectionRoute';
const TABS = ['Today', 'Titles', 'Entitlements', 'Earnings'] as const;
export function AuthorOverview() {
  const [tab, setTab] = useSectionRoute('/author', TABS);
  const unpublished = authorTitles.filter(title => title.status !== 'Published');
  const best = [...authorTitles].sort((a, b) => b.earningsNaira - a.earningsNaira)[0];
  const quiet = [...authorTitles].filter(title => title.status === 'Published').sort((a, b) => a.earningsNaira - b.earningsNaira)[0];
  return <div className="space-y-6" data-id="element-8639" data-mp="gq82pq5byiu2">
      <PageHeader eyebrow="Knowledge marketplace" title={authorProfile.name} description={`${authorProfile.titles} titles · ${authorProfile.readers.toLocaleString()} readers reached through Campuzon`} actions={<Badge tone="ok" data-id="element-8641" data-mp="vm5u6v4b6yzy">Verified publisher</Badge>} data-id="element-8640" data-mp="lp15anj2wpfg" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Author sections" data-id="element-8642" data-mp="zosqb880j62y" />

      {tab === 'Today' && <AttentionBoard emptyNote="Every title is published and nothing is waiting on a decision from you." items={[...unpublished.map(title => ({
      id: title.id,
      title: `${title.title} is not published`,
      detail: 'Students cannot buy or borrow it while it sits in this state, and it earns nothing.',
      meta: `${title.edition} · ${title.status}`,
      urgency: 'now' as const
    })), ...(quiet ? [{
      id: `quiet-${quiet.id}`,
      title: `${quiet.title} is your quietest published title`,
      detail: 'Published but barely reaching anyone. Check that its courses and edition are correct before assuming demand is the problem.',
      meta: `${naira(quiet.earningsNaira)} · ${quiet.purchases} purchases`,
      urgency: 'watch' as const
    }] : []), {
      id: 'payout',
      title: 'Earnings are awaiting payout',
      detail: 'Payout execution sits with a licensed provider. Campuzon records what you are owed; it does not hold it.',
      meta: `${naira(authorEarnings.pendingPayoutNaira)} pending`,
      urgency: 'watch' as const
    }]} summary={[{
      label: 'Net earnings',
      value: naira(authorEarnings.netNaira),
      note: `After ${naira(authorEarnings.platformFeeNaira)} platform fee.`
    }, {
      label: 'Best performing',
      value: best?.title ?? '—',
      note: best ? `${naira(best.earningsNaira)} to date.` : undefined
    }, {
      label: 'Readers reached',
      value: authorProfile.readers.toLocaleString(),
      note: 'Distinct entitlements, not page views.'
    }]} data-id="element-8643" data-mp="14fsn8dh4to5g" />}

      {tab === 'Titles' && <Panel title="Titles and editions" bodyClassName="p-0" data-id="element-8644" data-mp="1ax6qdymws1p2">
          <ul className="divide-y divide-line" data-id="element-8645" data-mp="1azt5qkg1jlhu">
            {authorTitles.map(title => <li key={title.id} className="px-5 py-4" data-id="element-8646" data-mp="30b3hpizj34b2">
                <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-8647" data-mp="35aoh1uim1eob">
                  <div className="min-w-0" data-id="element-8648" data-mp="15a01lwhzov38">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-8649" data-mp="157nktmnywqrr">
                      <p className="text-[14px] font-semibold text-ink" data-id="element-8650" data-mp="10byhr9cqorqp">{title.title}</p>
                      <Badge data-id="element-8651" data-mp="w80kz5avzt0t">{title.kind}</Badge>
                      <Badge tone={title.status === 'Published' ? 'ok' : 'warn'} data-id="element-8652" data-mp="wawhqqb00bno">
                        {title.status}
                      </Badge>
                    </div>
                    <p className="mt-1 text-[12px] text-ink-muted" data-id="element-8653" data-mp="mc6wqont2b37">{title.edition}</p>
                    <p className="mt-1 text-[13px] text-ink-muted" data-id="element-8654" data-mp="him83n83qtgo">{title.models}</p>
                  </div>
                  <div className="cz-num shrink-0 text-right text-[12px] text-ink-muted" data-id="element-8655" data-mp="clm4diyq62sq">
                    <p className="text-[13px] font-semibold text-ink" data-id="element-8656" data-mp="8l25oayi6r4h">
                      {naira(title.earningsNaira)}
                    </p>
                    <p className="mt-0.5" data-id="element-8657" data-mp="3uqpdrfvq50yy">
                      {title.rentals} rentals · {title.purchases} purchases
                    </p>
                    <p data-id="element-8658" data-mp="2a7v8dudjq0df">{title.activeEntitlements} active entitlements</p>
                  </div>
                </div>
              </li>)}
          </ul>
          <div className="border-t border-line px-5 py-3" data-id="element-8659" data-mp="2k741ym4i1cz9">
            <Button size="sm" variant="secondary" data-id="element-8660" data-mp="2k7m23tua8bcg">
              Upload a new edition
            </Button>
          </div>
        </Panel>}

      {tab === 'Entitlements' && <div className="space-y-6" data-id="element-8661" data-mp="1rjqav4nhfbn2">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-8662" data-mp="1rgcag1ydpci9">
            {authorEntitlementSummary.map(item => <div key={item.label} className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-8663" data-mp="20ibocle3k0gz">
                <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-8664" data-mp="25a3ewvx96674">
                  {item.label}
                </p>
                <p className="cz-num mt-2 text-2xl font-semibold text-ink" data-id="element-8665" data-mp="18rfezur8wdyg">
                  {item.value.toLocaleString()}
                </p>
                <p className="mt-1.5 text-[12px] leading-5 text-ink-muted" data-id="element-8666" data-mp="2g3n2e2n3lx7d">{item.note}</p>
              </div>)}
          </div>
          <Panel title="Payment and access are separate systems" data-id="element-8667" data-mp="2b4k9fgke7hfr">
            <p className="text-[13px] leading-6 text-ink-muted" data-id="element-8668" data-mp="2pu9dpkns81k9">
              A purchase or rental records a transaction; a separate entitlement decides whether a
              reader may currently open the resource. When a rental period ends, the entitlement
              expires while the transaction record remains intact for accounting, reconciliation and
              dispute handling. Download, print and territory permissions are set per licence and are
              never inferred from the payment.
            </p>
          </Panel>
        </div>}

      {tab === 'Earnings' && <div className="grid gap-6 sm:grid-cols-2" data-id="element-8669" data-mp="3hke6a6lp3uwk">
          <Panel title="Earnings to date" data-id="element-8670" data-mp="3hn8p5tp297nv">
            <dl className="divide-y divide-line" data-id="element-8671" data-mp="3dk3si7rnw075">
              {[{
            label: 'Gross sales and rentals',
            value: authorEarnings.grossNaira
          }, {
            label: 'Campuzon fee',
            value: authorEarnings.platformFeeNaira
          }, {
            label: 'Net earnings',
            value: authorEarnings.netNaira
          }, {
            label: 'Pending payout',
            value: authorEarnings.pendingPayoutNaira
          }].map(row => <div key={row.label} className="flex items-center justify-between gap-4 py-2.5" data-id="element-8672" data-mp="3g4igqseq9y8x">
                  <dt className="text-[13px] text-ink-muted" data-id="element-8673" data-mp="7op2kk7vcyv1">{row.label}</dt>
                  <dd className="cz-num text-[13px] font-semibold text-ink" data-id="element-8674" data-mp="cm8e3cd6mxja">{naira(row.value)}</dd>
                </div>)}
            </dl>
          </Panel>
          <Panel title="Payouts" data-id="element-8675" data-mp="2x70fsu40s8h">
            <p className="text-[13px] leading-6 text-ink-muted" data-id="element-8676" data-mp="2wf58j4fcwiq">
              Payouts follow the same settlement, identity and risk rules as every other marketplace
              vertical, and are disbursed by a licensed payout provider rather than by Campuzon.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-3" data-id="element-8677" data-mp="2rhfrmivpdmay">
              <Button variant="primary" disabled={!isLive('payouts')} data-id="element-8678" data-mp="2mpnv7rpv8mcw">
                Request payout
              </Button>
              <FeatureGate featureKey="payouts" inline data-id="element-8679" data-mp="2ipdrymtgka1q" />
            </div>
            <div className="mt-4" data-id="element-8680" data-mp="2dq15422l72z5">
              <FeatureGate featureKey="payouts" data-id="element-8681" data-mp="2dr2hc9vc9vig" />
            </div>
          </Panel>
        </div>}
    </div>;
}